import { usePaginatedSearch } from '@src/packages/utils/hooks/usePaginatedSearch';
import UserService from '@src/services/UserService';
import { useMemo } from 'react';

/**
 * Hook for managing schedule templates with search and pagination.
 *
 * @param {Object} params
 * @param {string} params.orgId - Organization ID
 * @param {string[]} [params.divisionIds=[]] - Division IDs to filter by
 * @param {string[]} [params.templateIds=[]] - Template IDs to preload
 * @param {boolean} [params.exactMatch=false] - Whether to match search term exactly
 * @param {number} [params.pageSize=10] - Number of templates per page
 *
 * @returns {Object} Hook return value
 * @returns {Array} templates - Array of template objects
 * @returns {Object} templatesById - Map of templates by ID
 * @returns {boolean} loading - Whether initial fetch is in progress
 * @returns {boolean} loadingMore - Whether pagination fetch is in progress
 * @returns {boolean} hasMore - Whether more templates are available
 * @returns {number} total - Total number of templates
 * @returns {Function} loadMore - Load next page of templates
 * @returns {Function} search - Search templates by term (debounced)
 * @returns {Function} reset - Reset to initial state
 */
export const useUserSelector = ({ orgId, divisionIds = [], userIds = [], pageSize = 10 }) => {
  const service = useMemo(() => new UserService(), []);

  return usePaginatedSearch({
    fetchFn: ({ from, pageSize: size, searchTerm, ids }) =>
      service.searchElastic({ orgId, divsIds: divisionIds, pageSize: size, from, searchTerm, ids }),
    parseResponse: result => ({
      items: result?.hits?.hits?.map(hit => ({ ...hit._source, id: hit._id })) || [],
      total: result?.hits?.total?.value || 0,
    }),
    initialIds: userIds,
    deps: [orgId, JSON.stringify(divisionIds)],
    pageSize,
    enabled: !!orgId,
  });
};
